import React from 'react';
import { useLocation } from 'react-router-dom';
import { useOptionalLanguage } from '../contexts/LanguageContext';
import { isNetworkLoadError, resetFailedLazies } from '../utils/lazyWithRetry';

const COPY = {
  offline: {
    title: { en: "You're offline", ar: 'أنت غير متصل بالإنترنت' },
    body: { en: 'Check your connection, then try again.', ar: 'تحقّق من اتصالك ثم حاول مرة أخرى.' },
  },
  chunk: {
    title: { en: "Couldn't load this page", ar: 'تعذّر تحميل هذه الصفحة' },
    body: { en: 'Check your connection and try again.', ar: 'تحقّق من اتصالك وحاول مرة أخرى.' },
  },
  other: {
    title: { en: 'Something went wrong', ar: 'حدث خطأ ما' },
    body: { en: 'Please try again. If it keeps happening, refresh the page.', ar: 'يرجى المحاولة مرة أخرى. إذا تكرّر ذلك، حدّث الصفحة.' },
  },
  retry: { en: 'Try again', ar: 'حاول مرة أخرى' },
};

const ErrorState = ({ kind, onRetry }) => {
  const lang = useOptionalLanguage();
  const ar = lang?.language === 'ar';
  const text = (pair) => (ar ? pair.ar : pair.en);
  const copy = COPY[kind];
  return (
    <section role="alert" data-testid="route-error" className="luxury-stage-wide flex flex-col items-center px-4 py-section text-center">
      <div className="h-px w-12 bg-crimson mb-6" aria-hidden="true" />
      <h1 className="text-2xl font-extrabold text-charcoal dark:text-white">{text(copy.title)}</h1>
      <p className="mt-2 max-w-md text-sm text-stone dark:text-white/80">{text(copy.body)}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 min-h-[44px] rounded-full bg-crimson px-6 text-[14px] font-bold text-white hover:bg-crimson-deep"
      >
        {text(COPY.retry)}
      </button>
    </section>
  );
};

class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== 'production') console.error('RouteErrorBoundary caught:', error, info);
  }

  componentDidUpdate(prev) {
    // Another page: start clean.
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.retry();
  }

  retry = () => {
    resetFailedLazies();
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.silent) return null;
    let kind = 'other';
    if (isNetworkLoadError(error)) kind = navigator.onLine === false ? 'offline' : 'chunk';
    return <ErrorState kind={kind} onRetry={this.retry} />;
  }
}

/**
 * Error boundary around the routes (inside <main>): a page that fails
 * (a lazy chunk that cannot load offline, a render error) shows a small
 * in-page message with "Try again", and the header and footer stay usable.
 * Resets on navigation. `silent`: optional chrome (footer, floating
 * buttons, pop-ups) that fails renders nothing instead of taking the page.
 */
export default function RouteErrorBoundary({ children, silent = false }) {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname} silent={silent}>{children}</Boundary>;
}
